import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';

/**
 * Hook to send an email draft
 */
export function useSendEmailDraft(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    ...orpc.communications.emailDrafts.send.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['communications', 'emailDrafts'],
      });
      // Invalidate email threads so the sent email appears in the thread view
      queryClient.invalidateQueries({
        queryKey: orpc.communications.emailThreads.getThread.key(),
      });
      // Invalidate AI suggestion queries using correct oRPC key
      queryClient.invalidateQueries({
        queryKey: orpc.ai.suggestions.get.key(),
      });
      queryClient.invalidateQueries({
        queryKey: ['accounts-receivable', 'sales-order-drafts'],
      });
      // Invalidate activity log to show the action
      queryClient.invalidateQueries({
        queryKey: orpc.core.activityLog.list.key(),
      });
      showToast({
        title: 'Email sent',
        message: 'The email was sent successfully',
        color: 'success',
        icon: 'check',
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Failed to send email',
        message:
          error.message ||
          'An unexpected error occurred while sending the email',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
